import React, { useState } from "react";
import { Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TemplateTextArea } from "./TemplateTextArea";
import { useCreateFAQ } from "./hooks/faqs.hooks";

interface CreateFAQItemProps {
  categoryId: string;
  onCancel: () => void;
  onSuccess?: () => void;
}

export const CreateFAQItem: React.FC<CreateFAQItemProps> = ({
  categoryId,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const createFAQ = useCreateFAQ();
  const [data, setData] = useState({
    question: "",
    answer: "",
  });
  const normalizedQuestion = data.question.trim();
  const normalizedAnswer = data.answer.trim();
  const showQuestionEmptyError =
    data.question.length > 0 && normalizedQuestion.length === 0;

  const handleSave = () => {
    if (!normalizedQuestion || !normalizedAnswer) {
      return;
    }

    if (categoryId) {
      createFAQ.mutate(
        {
          ...data,
          question: normalizedQuestion,
          answer: normalizedAnswer,
          categoryId,
          isPriority: false,
          isArchived: false,
        },
        {
          onSuccess: () => {
            onSuccess?.();
            onCancel();
          },
        },
      );
    }
  };

  return (
    <div
      className="border-base-border bg-secondary-badge flex flex-col gap-4 rounded-xl border p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="text-sm font-semibold text-black/96">{t("Create FAQ")}</h3>
      <Input
        label={t("Question")}
        placeholder={t("Question")}
        value={data.question}
        onChange={(e) => setData({ ...data, question: e.target.value })}
        aria-invalid={showQuestionEmptyError}
      />
      {showQuestionEmptyError ? (
        <p className="text-custom-destructive-dark text-xs">
          {t("Question cannot be empty")}
        </p>
      ) : null}
      <TemplateTextArea
        label={t("Answer")}
        placeholder={t("Answer")}
        value={data.answer}
        onChange={(val) => setData({ ...data, answer: val })}
        hideVariableSelector
        actions={
          <div className="flex justify-end gap-2">
            <Button variant="new_outline" onClick={onCancel}>
              <X /> {t("Cancel")}
            </Button>
            <Button
              variant="new_default"
              onClick={handleSave}
              disabled={
                !normalizedQuestion || !normalizedAnswer || createFAQ.isPending
              }
            >
              <Check /> {t("Save")}
            </Button>
          </div>
        }
      />
    </div>
  );
};
